import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { fetchPrices, fetchTicker, fetchHotNews, fetchLatestAnalysis, fetchMarketSummary, formatUSD, normalizeAnalysis, normalizeMarketContext, mergeMarketContext } from "../lib/api";
import useSchedule from "../hooks/useSchedule";
import { useI18n } from "../i18n";
import PriceTicker from "../components/PriceTicker";
import CoinPanel from "../components/CoinPanel";
import PriceChart from "../components/PriceChart";
import AIAnalysis from "../components/AIAnalysis";
import HotNewsHero from "../components/HotNewsHero";
import MarketSummary from "../components/MarketSummary";
import LanguageSelector from "../components/LanguageSelector";
import TechnicalChart from "../components/TechnicalChart";
import InstallPWA from "../components/InstallPWA";
import AlertBell from "../components/AlertBell";
import DecisionTrackTabs from "../components/DecisionTrackTabs";
import { Activity, ArrowUp } from "lucide-react";

const COINS = ["BTC", "ETH", "SOL", "HYPE"];

export default function Dashboard() {
  const { t, lang } = useI18n();
  const [prices, setPrices] = useState([]);
  const [ticker, setTicker] = useState([]);
  const [hotNews, setHotNews] = useState([]);
  const [selected, setSelected] = useState("BTC");
  const [lastUpdate, setLastUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signals, setSignals] = useState({}); // { BTC: {signal, confidence, entry_price}, ... }
  const [marketContext, setMarketContext] = useState(null);
  const [marketContextLoading, setMarketContextLoading] = useState(true);
  const { schedule, statusAvailable, now } = useSchedule();
  const signalsRequestRef = useRef(false);
  const selectedAnalysisRequestRef = useRef(false);
  const marketContextRequestRef = useRef(false);
  const marketContextRef = useRef(null);
  const processedLang = useRef(null);
  const middleColumnRef = useRef(null);
  const siteHeaderRef = useRef(null);
  const [middleColumnHeight, setMiddleColumnHeight] = useState(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const [siteHeaderHeight, setSiteHeaderHeight] = useState(0);
  const [requestedSharedTab, setRequestedSharedTab] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const updateBackToTopVisibility = () => setShowBackToTop(window.scrollY >= 500);
    updateBackToTopVisibility();
    window.addEventListener("scroll", updateBackToTopVisibility, { passive: true });
    return () => window.removeEventListener("scroll", updateBackToTopVisibility);
  }, []);

  const scrollToDashboardTop = () => {
    window.scrollTo({ top: 0, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  };

  useLayoutEffect(() => {
    const header = siteHeaderRef.current;
    if (!header) return undefined;

    const measureHeader = () => {
      const height = Math.ceil(header.getBoundingClientRect().height);
      setSiteHeaderHeight((current) => current === height ? current : height);
      document.documentElement.style.setProperty("--site-header-height", `${height}px`);
    };

    measureHeader();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measureHeader) : null;
    observer?.observe(header);
    window.addEventListener("resize", measureHeader);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measureHeader);
      document.documentElement.style.removeProperty("--site-header-height");
    };
  }, []);

  useLayoutEffect(() => {
    const media = window.matchMedia("(min-width: 1024px)");
    const updateViewport = () => setIsDesktop(media.matches);
    const measure = () => {
      const node = middleColumnRef.current;
      if (node && media.matches) setMiddleColumnHeight(node.getBoundingClientRect().height);
      else if (!media.matches) setMiddleColumnHeight(null);
    };
    const handleResize = () => { updateViewport(); measure(); };
    updateViewport();
    measure();
    const observer = middleColumnRef.current && typeof ResizeObserver !== "undefined" && new ResizeObserver(measure);
    if (observer) observer.observe(middleColumnRef.current);
    window.addEventListener("resize", handleResize);
    media.addEventListener?.("change", handleResize);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", handleResize);
      media.removeEventListener?.("change", handleResize);
    };
  }, [selected, lang]);

  const refreshPrices = async () => {
    try {
      const [p, tk] = await Promise.all([fetchPrices(), fetchTicker()]);
      setPrices(p); setTicker(tk);
      setLastUpdate(new Date());
    } catch (e) { console.error("prices", e); }
  };
  const refreshHot = async () => {
    try { setHotNews(await fetchHotNews()); } catch (e) { console.error(e); }
  };
  const refreshSignals = async () => {
    if (signalsRequestRef.current) return;
    signalsRequestRef.current = true;
    try {
      const res = await Promise.all(COINS.map((c) =>
        fetchLatestAnalysis(c, lang).then((a) => [c, a]).catch(() => [c, null])
      ));
      setSignals((current) => Object.fromEntries(res.map(([coin, analysis]) => [coin, normalizeAnalysis(analysis) || current[coin] || null])));
    } catch (e) { console.error(e); }
    finally { signalsRequestRef.current = false; }
  };
  const refreshSelectedAnalysis = async () => {
    if (selectedAnalysisRequestRef.current) return;
    selectedAnalysisRequestRef.current = true;
    try {
      const analysis = await fetchLatestAnalysis(selected, lang);
      setSignals((current) => ({ ...current, [selected]: normalizeAnalysis(analysis) }));
    } catch (e) { /* Preserve the last valid cached analysis during downtime. */ }
    finally { selectedAnalysisRequestRef.current = false; }
  };
  const refreshMarketContext = async () => {
    if (marketContextRequestRef.current) return;
    marketContextRequestRef.current = true;
    try {
      const data = normalizeMarketContext(await fetchMarketSummary());
      setMarketContext((current) => {
        const merged = mergeMarketContext(current, data);
        marketContextRef.current = merged;
        return merged;
      });
      setMarketContextLoading(false);
    } catch (e) {
      // Keep the last valid context visible if the backend is temporarily unavailable.
      setMarketContextLoading((current) => current && !marketContextRef.current);
    } finally { marketContextRequestRef.current = false; }
  };

  useEffect(() => {
    // Also ignores React StrictMode's development-only effect replay.
    if (processedLang.current === lang) return;
    const initialLoad = processedLang.current === null;
    processedLang.current = lang;

    if (initialLoad) {
      (async () => {
        setLoading(true);
        await Promise.all([refreshPrices(), refreshHot(), refreshSignals(), refreshMarketContext()]);
        setLoading(false);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    const pi = setInterval(refreshPrices, 8_000);
    const hi = setInterval(refreshHot, 120_000);
    const si = setInterval(refreshSignals, 5 * 60 * 1000);
    const mi = setInterval(refreshMarketContext, 60_000);
    return () => { clearInterval(pi); clearInterval(hi); clearInterval(si); clearInterval(mi); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previousScheduleRef = useRef(null);
  useEffect(() => {
    if (!schedule) return;
    const previous = previousScheduleRef.current;
    const technicalChanged = previous && (
      (previous.technical.is_running && !schedule.technical.is_running) ||
      previous.technical.last_completed_at !== schedule.technical.last_completed_at
    );
    const contextChanged = previous && (
      (previous.market_context.is_running && !schedule.market_context.is_running) ||
      previous.market_context.last_completed_at !== schedule.market_context.last_completed_at
    );
    previousScheduleRef.current = schedule;
    if (technicalChanged) refreshSelectedAnalysis();
    if (contextChanged) refreshMarketContext();
    // These actions are deliberately limited to completed-status transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule]);

  // Entry-price alerts: when live price is within 0.5% of AI entry_price, fire browser Notification once per signal
  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    if (!prices.length) return;

    const FIRED_KEY = "cryptoai_alerts_fired_v1";
    const fired = new Set(JSON.parse(localStorage.getItem(FIRED_KEY) || "[]"));

    for (const p of prices) {
      const a = signals[p.symbol];
      if (!a || !a.entry_price || !a.generated_at) continue;
      if (a.signal === "AVOID") continue;
      const alertId = `${p.symbol}:${a.generated_at}`;
      if (fired.has(alertId)) continue;
      const diffPct = Math.abs(p.price - a.entry_price) / a.entry_price;
      if (diffPct <= 0.005) { // within 0.5%
        try {
          new Notification(`${t("alert_hit_title")} · ${p.symbol}`, {
            body: `${p.symbol} ${t("alert_hit_body")} $${Number(a.entry_price).toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
            icon: "/logo192.png",
            tag: alertId,
          });
        } catch (e) { /* noop */ }
        fired.add(alertId);
      }
    }
    // Trim to last 40 to avoid unbounded growth
    const arr = Array.from(fired).slice(-40);
    localStorage.setItem(FIRED_KEY, JSON.stringify(arr));
    // eslint-disable-next-line
  }, [prices, signals]);

  const selectedPrice = prices.find((p) => p.symbol === selected);

  return (
    <div
      className="min-h-screen bg-[#050505] text-white bg-grid"
      style={{ "--site-header-height": `${siteHeaderHeight}px`, paddingTop: "var(--site-header-height)" }}
    >
      {/* Top bar */}
      <header ref={siteHeaderRef} className="fixed top-0 left-0 right-0 w-full z-50 isolate border-b border-border/60 bg-[#050505] overflow-x-clip">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2 sm:gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <img
              src="/crypto-ai-watch-logo.png"
              alt="Crypto AI Watch"
              className="w-8 h-8 rounded-sm object-contain shrink-0"
            />
            <div>
              <div className="font-display text-lg font-black leading-none">CRYPTO<span className="text-emerald-400">.</span>AI</div>
              <div className="text-[10px] text-zinc-500 font-mono tracking-widest uppercase">{t("tagline")}</div>
              <div className="text-[9px] text-emerald-500/70 font-mono tracking-wider mt-0.5" data-testid="made-by">{t("made_by")}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
            <div className="hidden md:flex items-center gap-2 text-xs text-zinc-400 font-mono" data-testid="auto-refresh-indicator">
              <span className="pulse-dot" />
              <span>{t("live")} · {lastUpdate ? lastUpdate.toLocaleTimeString() : "…"}</span>
            </div>
            <div className="hidden lg:flex items-center gap-1 text-xs text-zinc-500 font-mono">
              <Activity className="w-3 h-3" strokeWidth={1.5} /> OKX · RSS · OpenRouter
            </div>
            <LanguageSelector />
            <AlertBell />
            <InstallPWA />
          </div>
        </div>
        <PriceTicker prices={ticker} />
      </header>

      {/* Market-wide featured news */}
      <section className="max-w-[1600px] mx-auto px-4 sm:px-6 pt-4 sm:pt-6">
        <HotNewsHero items={hotNews} loading={loading && hotNews.length === 0} />
      </section>

      {/* Market Summary */}
      <section className="max-w-[1600px] mx-auto px-4 sm:px-6 pt-4">
        <MarketSummary data={marketContext} loading={marketContextLoading} schedule={schedule} now={now} statusAvailable={statusAvailable} />
      </section>

        {/* Assets, chart, technical snapshot and coin analysis */}
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 py-4 sm:py-6 grid grid-cols-12 gap-4 lg:items-stretch">
        <div className="col-span-12 text-[11px] font-mono tracking-widest text-zinc-500 uppercase">{t("assets")}</div>
        <div id="assets-home" className="dashboard-scroll-target col-span-12 lg:hidden" data-testid="mobile-coin-selector">
          <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {COINS.map((c) => {
              const coin = prices.find((p) => p.symbol === c);
              const positive = (coin?.change_24h ?? 0) >= 0;
              return (
                <button
                  key={c}
                  type="button"
                  aria-label={`${coin?.name || c} ${c}`}
                  aria-pressed={selected === c}
                  onClick={() => setSelected(c)}
                  data-testid={`mobile-coin-${c.toLowerCase()}`}
                  className={`min-w-[88px] flex-1 rounded-sm border px-2 py-2 text-left transition-colors ${
                    selected === c
                      ? "border-white/40 bg-white/[0.08]"
                      : "border-border/60 bg-[#09090B] hover:border-border hover:bg-white/[0.03]"
                  }`}
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    {coin?.image ? (
                      <img src={coin.image} alt="" className="w-4 h-4 rounded-full shrink-0" />
                    ) : (
                      <span className="w-4 h-4 rounded-full bg-zinc-800 shrink-0" aria-hidden="true" />
                    )}
                    <span className="text-[10px] font-mono font-bold truncate">{c}</span>
                  </div>
                  <div className="mt-1 text-[11px] font-mono text-white truncate">
                    {coin ? formatUSD(coin.price) : "-"}
                  </div>
                  <div className={`text-[10px] font-mono ${positive ? "text-emerald-400" : "text-red-400"}`}>
                    {coin ? `${positive ? "+" : ""}${coin.change_24h.toFixed(2)}%` : "-"}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <aside className="dashboard-scroll-target hidden lg:col-span-3 lg:min-w-0 lg:flex lg:flex-col lg:gap-2 lg:space-y-0">
          {COINS.map((c) => {
            const pr = prices.find((p) => p.symbol === c);
            return (
              <CoinPanel
                key={c}
                symbol={c}
                data={pr}
                active={selected === c}
                onClick={() => setSelected(c)}
              />
            );
          })}
        </aside>
        <section ref={middleColumnRef} className="col-span-12 lg:col-span-6 lg:min-w-0 flex flex-col gap-4 min-h-0">
            <nav aria-label={t("mobile_navigation")} className="mobile-section-nav lg:hidden">
              <a href="#technical-analysis" title={t("technical_analysis")} aria-label={t("technical_analysis")}>{t("technical")}</a>
              <a href="#decision-track-tabs" title={t("market_decision_guide")} aria-label={t("market_decision_guide")} onClick={() => setRequestedSharedTab("decision")}>{t("decision")}</a>
              <a href="#coin-news-list" title={`${t("news")} · ${selected}`} aria-label={`${t("news")} · ${selected}`} onClick={() => setRequestedSharedTab("news")}>{t("news")}</a>
            </nav>
            <PriceChart symbol={selected} price={selectedPrice} />
            <section id="technical-snapshot" className="dashboard-scroll-target">
              <TechnicalChart symbol={selected} analysis={signals[selected]} livePrice={selectedPrice?.price} />
            </section>
        </section>
        <section id="ai-analyst" className="dashboard-scroll-target col-span-12 lg:col-span-3 lg:min-w-0 lg:min-h-0">
            <AIAnalysis
              symbol={selected}
              analysis={signals[selected]}
              marketContext={marketContext}
              schedule={schedule}
              now={now}
              statusAvailable={statusAvailable}
              desktopHeight={isDesktop ? middleColumnHeight : null}
            />
        </section>
      </main>

      {/* Selected-coin decision guidance and historical accountability */}
      <section className="max-w-[1600px] mx-auto px-4 sm:px-6 pt-1 pb-4" data-testid="market-decision-section">
        <DecisionTrackTabs selected={selected} analyses={signals} prices={prices} marketContext={marketContext} schedule={schedule} statusAvailable={statusAvailable} onSelect={setSelected} requestedTab={requestedSharedTab} />
      </section>

      <footer className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 sm:py-8 text-xs text-zinc-600 font-mono">
        <div>{t("disclaimer")}</div>
        <div className="mt-1 text-zinc-500" data-testid="copyright">{t("copyright")}</div>
      </footer>

      {showBackToTop && (
        <button
          type="button"
          className="mobile-home-link fixed z-40 w-auto max-w-[calc(100vw-2rem)] sm:right-6 sm:bottom-6 inline-flex items-center justify-center gap-1.5 shadow-lg shadow-black/30"
          aria-label={t("back_to_top")}
          onClick={scrollToDashboardTop}
        >
          <ArrowUp className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          <span>{t("back_to_top")}</span>
        </button>
      )}
    </div>
  );
}
